import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { BarChart3, TrendingUp, Lock, ShoppingCart, Users, FileText, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

export default function SchoolAnalytics() {
  const [modules, setModules] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const mod = modules.find((m) => m.key === "school_analytics");
  const owned = mod?.owned;

  useEffect(() => {
    api.get(`${V1}/school-modules`).then((r) => setModules(r.data || []));
  }, []);

  useEffect(() => {
    if (!owned) return;
    api.get(`${V1}/school/analytics/summary`).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Yüklenemedi"));
  }, [owned]);

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;

  if (!owned) return <LockedModule mod={mod} testid="analytics-locked" />;

  if (error) return <div className="p-6 rounded-xl border border-destructive/40 bg-destructive/5 text-destructive text-sm">{error}</div>;
  if (!data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="school-analytics-page">
      <div>
        <div className="overline text-accent">Modül · {mod.label}</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-primary" /> Başvuru Analitiği
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Okulunuza gelen başvuruların canlı özet paneli.</p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Metric icon={FileText} label="Toplam Başvuru" value={data.totals.applications} testid="a-apps" />
        <Metric icon={Users} label="Gönderilen Davet" value={data.totals.invitations} testid="a-inv" />
        <Metric icon={Award} label="Yayınlanan Sınav" value={data.totals.exams} testid="a-exams" />
        <Metric icon={TrendingUp} label="Dönüşüm Oranı" value={`%${data.totals.conversion_rate}`} testid="a-conv" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="font-display font-bold mb-3">Aylık Başvuru Trendi</div>
          {data.monthly.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.monthly}>
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="font-display font-bold mb-3">Durum Dağılımı</div>
          <div className="space-y-2">
            {Object.keys(data.status_counts).length === 0 && <div className="text-sm text-muted-foreground">Veri yok</div>}
            {Object.entries(data.status_counts).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="uppercase tracking-widest text-xs text-muted-foreground">{k}</span>
                <b className="font-mono" data-testid={`status-count-${k}`}>{v}</b>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="font-display font-bold mb-3">En Çok Başvuru Gelen İller</div>
          {data.top_provinces.length === 0 ? (
            <div className="text-sm text-muted-foreground">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_provinces}>
                <XAxis dataKey="province" fontSize={10} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-5 rounded-xl border border-border bg-card">
          <div className="font-display font-bold mb-3">Sınıf Bazlı Dağılım</div>
          <div className="space-y-2">
            {data.grade_breakdown.length === 0 && <div className="text-sm text-muted-foreground">Veri yok</div>}
            {data.grade_breakdown.map((g) => (
              <div key={g.grade} className="flex items-center justify-between text-sm">
                <span>{g.grade}. Sınıf</span>
                <b className="font-mono">{g.count}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, testid }) => (
  <div className="p-5 rounded-xl border border-border bg-card" data-testid={testid}>
    <Icon className="h-6 w-6 text-accent mb-2" />
    <div className="text-3xl font-display font-bold">{value}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

export function LockedModule({ mod, testid }) {
  return (
    <div className="max-w-2xl mx-auto py-8 text-center" data-testid={testid}>
      <div className="p-8 rounded-2xl border-2 border-dashed border-accent/40 bg-accent/5">
        <Lock className="h-12 w-12 mx-auto text-accent mb-3" />
        <h1 className="font-display text-2xl font-bold">{mod.label} Kilitli</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{mod.description}</p>
        <div className="grid grid-cols-2 gap-3 mt-6 max-w-xs mx-auto">
          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Aylık</div>
            <div className="text-2xl font-display font-bold">₺{mod.price_monthly}</div>
          </div>
          <div className="p-4 rounded-lg border border-accent/40 bg-accent/10">
            <div className="text-xs uppercase tracking-widest text-accent">Yıllık</div>
            <div className="text-2xl font-display font-bold">₺{mod.price_yearly}</div>
            <div className="text-[10px] text-accent">2 ay bedava</div>
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-center">
          <Button asChild>
            <a href={`mailto:destek@sanostudent.com?subject=Modül Talebi: ${mod.label}`}>
              <ShoppingCart className="h-4 w-4" /> Modülü Talep Et
            </a>
          </Button>
          <Button asChild variant="outline"><Link to="/okul-paneli/modullerim">Tüm Modüller</Link></Button>
        </div>
      </div>
    </div>
  );
}
